import axiosInstance from '../utils/axios';

/**
 * Percentile and leaderboard (monetization feature #02).
 *
 * Both are free surfaces — knowing where you placed is what makes a result mean
 * something, and it is the pull back for the next paper.
 *
 * A small cohort is a normal, expected state, not an error: the service returns a null
 * percentile rather than a made-up number when too few students have sat the paper.
 * The caller has to render that case, so it survives into the types here.
 */

export interface Percentile {
  packageId: string;
  score: number | null;
  /** Completed attempts with a usable score — the population measured against. */
  cohortSize: number;
  /** Null when the cohort is too small to say anything honest. */
  percentile: number | null;
  /** 1 = highest. Ties share a rank. */
  rank: number | null;
  /** Ready-made line, e.g. "You beat 82.0% of this cohort". Null with a small cohort. */
  callout: string | null;
}

export interface LeaderboardEntry {
  rank: number;
  /** Display name only — the API sends no phone or student id to this surface. */
  studentName: string;
  score: number | null;
  totalScore: number | null;
  percentage: number | null;
}

export interface Leaderboard {
  packageId: string;
  cohortSize: number;
  entries: LeaderboardEntry[];
}

export const fetchPercentile = async (
  packageId: string,
  score: number
): Promise<Percentile | null> => {
  try {
    const response = await axiosInstance.get(`/packages/${packageId}/percentile`, {
      params: { score }
    });
    if (response.data?.status === 'success' && response.data?.data) {
      return response.data.data as Percentile;
    }
    return null;
  } catch {
    return null;
  }
};

export const fetchLeaderboard = async (
  packageId: string,
  limit = 10
): Promise<Leaderboard | null> => {
  try {
    const response = await axiosInstance.get(`/leaderboards/${packageId}`, {
      params: { limit }
    });
    if (response.data?.status === 'success' && response.data?.data) {
      return response.data.data as Leaderboard;
    }
    return null;
  } catch {
    return null;
  }
};
